/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values had been redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy required touching all those places and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the rest of the durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * `staleTime` default for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale
 * data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It is kept as its own constant (even though today it matches the
 * offline cache value) because it is an independent decision: it must stay
 * aligned between `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Timeout ceiling for a `get-session` performed INSIDE a route `beforeLoad`.
 *
 * P23/CC-60 · I1-bis: a `beforeLoad` that `await`s the network without a ceiling does not fail
 * with a 401 or 503 (both resolve quickly, and a 5xx falls into
 * SUCCESS with `null` because of `openapi-fetch`'s `?? null`) — it fails when the
 * response is STALLED: TanStack Router does not mount the route,
 * `__root`'s `<Suspense fallback={null}>` has nothing to render, and the whole
 * app stays BLANK for as long as the network takes. Measured on `/login`:
 * `innerText` empty at 3, 6, 10, 15 and 20 s, on the dev server AND on the
 * production bundle.
 *
 * Single source for both route guards (`_app` and `_auth`): keeping the ceiling
 * as a local literal inside `_app` was exactly what left `_auth` without
 * the defense `_app` had, which is why the I1 box came out green (it measured
 * `/`, which was healthy, not `/login`). Once the ceiling is passed the screen
 * is painted and the late response still synchronizes the machine and cache —
 * `BootManager` redirects afterward if a session was present.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/** Seconds before revoking the object URL for a download (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Margin before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably greater than the time the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
